import { useState, useRef, useEffect } from 'react'
import {
  downloadAsMarkdown,
  downloadAsText,
  downloadAsPDF,
  downloadAsRTF,
  downloadAsDocx,
} from '@/lib/download'

interface Format {
  id:    string
  label: string
  ext:   string
  async: boolean
}

const FORMATS: Format[] = [
  { id: 'md',   label: 'Markdown',   ext: '.md',   async: false },
  { id: 'txt',  label: 'Plain Text', ext: '.txt',  async: false },
  { id: 'rtf',  label: 'RTF',        ext: '.rtf',  async: false },
  { id: 'docx', label: 'Word',       ext: '.docx', async: true  },
  { id: 'pdf',  label: 'PDF',        ext: '',      async: false },
]

function DownloadIcon() {
  return (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
        d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
      />
    </svg>
  )
}

function Spinner() {
  return (
    <svg className="w-3.5 h-3.5 animate-spin" viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" opacity="0.25" />
      <path d="M12 2a10 10 0 0 1 10 10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
    </svg>
  )
}

interface DownloadMenuProps {
  content:  string
  filename: string
}

export function DownloadMenu({ content, filename }: DownloadMenuProps) {
  const [open, setOpen]       = useState(false)
  const [busy, setBusy]       = useState<string | null>(null)
  const [error, setError]     = useState<string | null>(null)
  const containerRef          = useRef<HTMLDivElement>(null)

  // Close on outside click
  useEffect(() => {
    if (!open) return
    const handler = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  const handle = async (fmt: Format) => {
    setError(null)
    setBusy(fmt.id)
    try {
      switch (fmt.id) {
        case 'md':   downloadAsMarkdown(content, filename); break
        case 'txt':  downloadAsText(content, filename);     break
        case 'rtf':  downloadAsRTF(content, filename);      break
        case 'docx': await downloadAsDocx(content, filename); break
        case 'pdf':  downloadAsPDF(); break
      }
      setOpen(false)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Export failed.')
    } finally {
      setBusy(null)
    }
  }

  return (
    <div ref={containerRef} className="relative">
      <button
        onClick={() => { setOpen(!open); setError(null) }}
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg
                   text-xs font-medium text-forge-ash
                   border border-forge-border
                   hover:text-forge-gold hover:border-forge-gold/40
                   transition-colors"
      >
        <DownloadIcon />
        Export
        <svg className={`w-3 h-3 transition-transform ${open ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-1.5 w-48 bg-forge-basalt border border-forge-border rounded-lg shadow-2xl shadow-black/50 z-50 overflow-hidden animate-fade-in">
          {error && (
            <p className="px-4 py-2 text-xs text-forge-heat-critical border-b border-forge-border">{error}</p>
          )}
          {FORMATS.map((fmt) => (
            <button
              key={fmt.id}
              onClick={() => void handle(fmt)}
              disabled={!!busy}
              className="w-full flex items-center justify-between px-4 py-2.5
                         text-sm text-forge-primary hover:bg-forge-slate
                         disabled:opacity-50 transition-colors"
            >
              <span>{fmt.label}</span>
              <span className="flex items-center gap-1.5 text-forge-ash text-xs font-mono">
                {busy === fmt.id ? <Spinner /> : fmt.ext}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
